import { NavLink } from 'react-router-dom';
import { useAuth, useContext } from '@/hooks';
import { cn } from '@/utils/cn';
import {
  LayoutDashboard,
  Calendar,
  Building2,
  Stethoscope,
  Users,
  UserPlus,
  FileText,
  X
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  requireRoles?: string[];
  requireContext?: 'clinic' | 'doctor' | 'full';
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    label: 'Clínicas',
    path: '/clinicas',
    icon: Building2,
  },
  {
    label: 'Médicos',
    path: '/medicos',
    icon: Stethoscope,
    requireContext: 'clinic',
  },
  {
    label: 'Calendário',
    path: '/calendar',
    icon: Calendar,
    requireContext: 'clinic',
  },
];

const adminItems: NavItem[] = [
  {
    label: 'Gerenciar Médicos',
    path: '/admin/medicos',
    icon: Stethoscope,
    requireRoles: ['Administrador'],
  },
  {
    label: 'Gerenciar Pacientes',
    path: '/admin/pacientes',
    icon: Users,
    requireRoles: ['Administrador'],
  },
  {
    label: 'Gerenciar Usuários',
    path: '/admin/usuarios',
    icon: UserPlus,
    requireRoles: ['Administrador'],
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { hasRole } = useAuth();
  const { hasContext, hasFullContext } = useContext();

  const canViewItem = (item: NavItem): boolean => {
    // Check role requirements
    if (item.requireRoles && item.requireRoles.length > 0) {
      const hasRequiredRole = item.requireRoles.some(role =>
        hasRole(role as any)
      );
      if (!hasRequiredRole) return false;
    }

    // Check context requirements
    if (item.requireContext === 'clinic' && !hasContext) return false;
    if (item.requireContext === 'doctor' && !hasFullContext) return false;
    if (item.requireContext === 'full' && !hasFullContext) return false;

    return true;
  };

  const NavSection = ({
    title,
    items
  }: {
    title?: string;
    items: NavItem[];
  }) => {
    const visibleItems = items.filter(canViewItem);

    if (visibleItems.length === 0) return null;

    return (
      <div className="mb-6">
        {title && (
          <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {title}
          </h3>
        )}
        <nav className="space-y-1">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => onClose()}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                )
              }
            >
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto lg:relative lg:inset-0 lg:z-0 transition-transform duration-300 ease-in-out lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="flex-1 px-3 mt-8 lg:mt-0">
          <NavSection items={navItems} />

          {hasRole('Administrador') && (
            <NavSection title="Administração" items={adminItems} />
          )}
        </div>

        {/* Sidebar footer */}
        <div className="px-3 pt-4 border-t border-gray-200">
          <NavLink
            to="/ajuda"
            onClick={() => onClose()}
            className={({ isActive }) =>
              cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              )
            }
          >
            <FileText className="mr-3 h-5 w-5 flex-shrink-0" />
            Ajuda & Suporte
          </NavLink>
        </div>
      </aside>
    </>
  );
}